import { AlertTriangle } from 'lucide-react'
import { Tooltip } from '@/components/ui/tooltip'

/**
 * Marks an event whose floor plans nobody has confirmed yet.
 *
 * An unconfirmed phase still live-binds to the venue's current space version, so
 * the plan can change under the event — a space remodeled or restocked moves
 * the furniture someone arranged. Confirming freezes it. That is worth a marker
 * on the way in rather than a surprise on the day.
 *
 * **Only for events still ahead**, and that is decided upstream: an event whose
 * date has passed reports zero unconfirmed plans, because its phases are frozen
 * by the date and can no longer be confirmed. A marker asking for something
 * impossible is the fastest way to teach people to ignore markers.
 *
 * So this component reads no clock and takes no date. It renders a count.
 */
/**
 * The marker's sentence, in plain words. It said "All floor plans are not
 * confirmed — they still track the venue, so a remodel can move the furniture",
 * which a reader taking it word for word could not follow ("track"? "remodel"?),
 * and "All … are not" reads as "not all".
 */
export function unconfirmedPlansMessage(unconfirmed: number, total: number): string {
  if (total === 1) return 'This floor plan isn’t confirmed yet. Until it is, changes to the room change it too.'
  const which = unconfirmed === total ? 'No floor plans are confirmed yet.' : `${String(unconfirmed)} of ${String(total)} floor plans aren’t confirmed yet.`
  return `${which} Until they are, changes to the rooms change them too.`
}

export function UnconfirmedPlansAlert({
  unconfirmed,
  total,
  className,
}: {
  unconfirmed: number
  total: number
  className?: string
}) {
  if (unconfirmed === 0) return null

  const message = unconfirmedPlansMessage(unconfirmed, total)

  return (
    <Tooltip content={message}>
      <span className={`ds-attention ${className ?? ''}`}>
        <AlertTriangle aria-hidden />
        {/* The icon can say "look here" and not why. Anyone who cannot hover
            gets the same sentence. */}
        <span className="sr-only">{message}</span>
      </span>
    </Tooltip>
  )
}
